import { useState } from "react";
import { format, setHours, setMinutes, startOfDay } from "date-fns";
import { CalendarDays, Clock } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = [0, 15, 30, 45];
const tz = Intl.DateTimeFormat().resolvedOptions().timeZone?.replace(/_/g, " ") || "local time";

export const DateTimePicker = ({ value, onChange, testId = "datetime" }) => {
  const [open, setOpen] = useState(false);
  const date = value || null;
  const h24 = date ? date.getHours() : 6;
  const hour12 = h24 % 12 === 0 ? 12 : h24 % 12;
  const ampm = h24 >= 12 ? "PM" : "AM";
  const minute = date ? MINUTES.reduce((a, b) => (Math.abs(b - date.getMinutes()) < Math.abs(a - date.getMinutes()) ? b : a)) : 0;

  const apply = (day, h12 = hour12, m = minute, ap = ampm) => {
    const hh = (h12 % 12) + (ap === "PM" ? 12 : 0);
    onChange(setMinutes(setHours(startOfDay(day || date || new Date()), hh), m));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="mt-3 flex w-full sm:w-auto items-center gap-3 rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-left hover:border-sky-400/60 transition-colors" data-testid={`${testId}-trigger`}>
          <CalendarDays size={18} className="text-sky-300 shrink-0" />
          <span className="min-w-0">
            <span className="block text-sm font-semibold" data-testid={`${testId}-display`}>{date ? format(date, "EEEE, MMMM d, yyyy") : "Pick a date"}</span>
            <span className="block text-xs text-slate-400">{date ? `${format(date, "h:mm a")} · ${tz}` : "and a time"}</span>
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0 card-surface border-white/10" data-testid={`${testId}-popover`}>
        <div className="flex flex-col sm:flex-row">
          <Calendar mode="single" selected={date || undefined} onSelect={(d) => d && apply(d)} initialFocus />
          <div className="border-t sm:border-t-0 sm:border-l border-white/10 p-4 sm:w-52 space-y-3">
            <p className="eyebrow flex items-center gap-1.5"><Clock size={12} /> Time</p>
            <div className="grid grid-cols-3 gap-1.5">
              <select value={hour12} onChange={(e) => apply(date, Number(e.target.value))} className="h-9 rounded-md bg-white/5 border border-white/10 px-2 text-sm" aria-label="Hour" data-testid={`${testId}-hour`}>{HOURS.map((h) => <option key={h} value={h}>{h}</option>)}</select>
              <select value={minute} onChange={(e) => apply(date, hour12, Number(e.target.value))} className="h-9 rounded-md bg-white/5 border border-white/10 px-2 text-sm" aria-label="Minutes" data-testid={`${testId}-minute`}>{MINUTES.map((m) => <option key={m} value={m}>{String(m).padStart(2, "0")}</option>)}</select>
              <div className="inline-flex rounded-md bg-white/5 border border-white/10 p-0.5" data-testid={`${testId}-ampm`}>
                {["AM", "PM"].map((ap) => <button key={ap} type="button" onClick={() => apply(date, hour12, minute, ap)} className={`flex-1 rounded px-1 text-xs font-bold ${ampm === ap ? "bg-sky-400 text-[#070F1E]" : "text-slate-300"}`} data-testid={`${testId}-${ap.toLowerCase()}`}>{ap}</button>)}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {[["6:00 AM", 6, 0], ["12:00 PM", 12, 0], ["5:00 PM", 17, 0]].map(([label, hh, mm]) => (
                <button key={label} type="button" onClick={() => onChange(setMinutes(setHours(startOfDay(date || new Date()), hh), mm))} className="rounded-full bg-white/5 px-2.5 py-1 text-[11px] font-semibold text-slate-300 hover:bg-white/10 hover:text-white" data-testid={`${testId}-preset-${hh}`}>{label}</button>
              ))}
            </div>
            <p className="text-[11px] text-slate-500">Times are in your local timezone ({tz}).</p>
            <button type="button" onClick={() => setOpen(false)} className="w-full rounded-full bg-sky-400 py-1.5 text-xs font-bold text-[#070F1E] hover:bg-sky-300" data-testid={`${testId}-done`}>Done</button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
